if (!customElements.get('sa-product-grid')) {
  let pgmScrollLockDepth = 0;
  let pgmScrollLockY = 0;

  function pgmLockPageScroll() {
    if (pgmScrollLockDepth === 0) {
      pgmScrollLockY = window.scrollY;
      document.documentElement.classList.add('sa-pgm-body-lock');
      document.body.classList.add('sa-pgm-body-lock');
      Object.assign(document.body.style, {
        position: 'fixed',
        top: `-${pgmScrollLockY}px`,
        left: '0',
        right: '0',
        width: '100%',
      });
    }
    pgmScrollLockDepth += 1;
  }

  function pgmUnlockPageScroll() {
    pgmScrollLockDepth = Math.max(0, pgmScrollLockDepth - 1);
    if (pgmScrollLockDepth > 0) return;
    document.documentElement.classList.remove('sa-pgm-body-lock');
    document.body.classList.remove('sa-pgm-body-lock');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    window.scrollTo(0, pgmScrollLockY);
  }

  class SaProductGrid extends HTMLElement {
    constructor() {
      super();
      this._onKeyDown = this._onKeyDown.bind(this);
    }

    connectedCallback() {
      this._setupTriggers();
      this._setupPopups();
      document.addEventListener('keydown', this._onKeyDown);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this._onKeyDown);
      this.querySelectorAll('.sa-pgm-overlay.is-open').forEach((overlay) => {
        this._closePopup(overlay);
      });
    }

    _onKeyDown(e) {
      const open = this.querySelector('.sa-pgm-overlay.is-open');
      if (!open) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        this._closePopup(open);
        return;
      }

      if (e.key === 'Tab') {
        this._handleTabKey(open, e);
      }
    }

    _handleTabKey(overlay, e) {
      const focusable = Array.from(
        overlay.querySelectorAll(
          'a[href]:not([disabled]), button:not([disabled]), input:not([disabled]), ' +
          'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => !el.closest('[aria-hidden="true"]') && el.offsetParent !== null);

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first || document.activeElement === overlay) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    _setupTriggers() {
      this.querySelectorAll('[data-popup-open]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const overlay = this.querySelector('#' + btn.dataset.popupOpen);
          if (overlay) this._openPopup(overlay);
        });
      });
    }

    _setupPopups() {
      this.querySelectorAll('.sa-pgm-overlay').forEach((overlay) => {
        const closeBtn = overlay.querySelector('.sa-pgm-close');
        if (closeBtn) {
          closeBtn.addEventListener('click', () => this._closePopup(overlay));
        }
        overlay.addEventListener('click', (e) => {
          if (e.target === overlay) this._closePopup(overlay);
        });
        this._initGallery(overlay);
        this._initSwatches(overlay);
        this._initQty(overlay);
        this._initATC(overlay);
      });
    }

    _openPopup(overlay) {
      overlay._previousFocus = document.activeElement;

      overlay.classList.add('is-open');
      overlay.removeAttribute('aria-hidden');
      pgmLockPageScroll();

      // Move focus to the close button (first interactive element)
      const closeBtn = overlay.querySelector('.sa-pgm-close');
      if (closeBtn) {
        requestAnimationFrame(() => closeBtn.focus());
      }
    }

    _closePopup(overlay) {
      overlay.classList.remove('is-open');
      overlay.setAttribute('aria-hidden', 'true');
      pgmUnlockPageScroll();

      // Restore focus to the element that triggered the modal
      if (overlay._previousFocus && overlay._previousFocus.focus) {
        overlay._previousFocus.focus();
        overlay._previousFocus = null;
      }
    }

    _getMoneyFmt(overlay) {
      return overlay.querySelector('.sa-pgm-modal')?.dataset.moneyFormat || '';
    }

    _getQty(overlay) {
      const el = overlay.querySelector('.sa-pgm-qty-num');
      return Math.max(1, parseInt(el?.value ?? el?.textContent ?? 1, 10) || 1);
    }

    _initGallery(overlay) {
      const slides = Array.from(overlay.querySelectorAll('.sa-pgm-slide'));
      const thumbs = Array.from(overlay.querySelectorAll('.sa-pgm-thumb'));
      if (slides.length < 2) return;

      let current = 0;

      const goTo = (idx) => {
        current = ((idx % slides.length) + slides.length) % slides.length;
        slides.forEach((el, i) => el.classList.toggle('active', i === current));
        thumbs.forEach((el, i) => el.classList.toggle('active', i === current));
        const active = thumbs[current];
        if (active) active.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      };

      const prevBtn = overlay.querySelector('.sa-pgm-arrow--prev');
      const nextBtn = overlay.querySelector('.sa-pgm-arrow--next');
      const moreBtn = overlay.querySelector('.sa-pgm-thumbs-more');
      if (prevBtn) prevBtn.addEventListener('click', () => goTo(current - 1));
      if (nextBtn) nextBtn.addEventListener('click', () => goTo(current + 1));
      if (moreBtn) moreBtn.addEventListener('click', () => goTo(current + 1));
      thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => goTo(i)));

      overlay._goTo = goTo;
    }

    _initSwatches(overlay) {
      const swatches = overlay.querySelectorAll('.sa-pgm-swatch');
      if (!swatches.length) return;
      const jsonEl = overlay.querySelector('.sa-pgm-variants-json');
      if (!jsonEl) return;

      const variants = JSON.parse(jsonEl.textContent);
      const selectedOpts = {};

      swatches.forEach((s) => {
        if (s.classList.contains('active')) {
          selectedOpts[s.dataset.optPos] = s.dataset.value;
        }
      });

      swatches.forEach((swatch) => {
        swatch.addEventListener('click', () => {
          const pos = swatch.dataset.optPos;
          const val = swatch.dataset.value;
          selectedOpts[pos] = val;

          overlay.querySelectorAll(`.sa-pgm-swatch[data-opt-pos="${pos}"]`).forEach((s) => {
            s.classList.toggle('active', s.dataset.value === val);
            s.setAttribute('aria-pressed', s.dataset.value === val ? 'true' : 'false');
          });

          const label = overlay.querySelector(`.sa-pgm-opt-label[data-opt-pos="${pos}"] .sa-pgm-opt-val`);
          if (label) label.textContent = val;

          const variant = variants.find((v) =>
            Object.entries(selectedOpts).every(([p, value]) => v['option' + p] === value)
          );
          if (variant) this._updateVariant(overlay, variant);

          if (swatch.dataset.imgIdx !== undefined && overlay._goTo) {
            overlay._goTo(parseInt(swatch.dataset.imgIdx, 10));
          }
        });
      });

      // Set initial aria-pressed state
      swatches.forEach((s) => {
        s.setAttribute('aria-pressed', s.classList.contains('active') ? 'true' : 'false');
      });
    }

    _updateVariant(overlay, variant) {
      const moneyFmt = this._getMoneyFmt(overlay);
      const qty = this._getQty(overlay);

      const currentPriceEl = overlay.querySelector('.sa-pgm-current-price');
      if (currentPriceEl) {
        currentPriceEl.dataset.price = variant.price;
        currentPriceEl.textContent = this._fmtMoney(variant.price, moneyFmt);
      }

      const atcBtn = overlay.querySelector('.sa-pgm-atc');
      if (atcBtn) {
        atcBtn.dataset.variantId = variant.id;
        atcBtn.disabled = !variant.available;
        atcBtn.setAttribute('aria-disabled', variant.available ? 'false' : 'true');
      }

      this._recalcTotal(overlay, variant.price, variant.compare_at_price, qty, moneyFmt);
    }

    _initQty(overlay) {
      const minus = overlay.querySelector('.sa-pgm-qty-minus');
      const plus = overlay.querySelector('.sa-pgm-qty-plus');
      const numEl = overlay.querySelector('.sa-pgm-qty-num');
      if (!numEl) return;

      const refresh = (qty) => {
        qty = Math.max(1, qty);
        numEl.value = qty;
        const price = parseInt(overlay.querySelector('.sa-pgm-current-price')?.dataset.price || 0, 10);
        const compare = parseInt(overlay.querySelector('.sa-pgm-compare-price-total')?.dataset.compare || 0, 10);
        this._recalcTotal(overlay, price, compare, qty, this._getMoneyFmt(overlay));
        if (minus) minus.disabled = qty <= 1;
      };

      if (minus) {
        minus.addEventListener('click', () => {
          const current = this._getQty(overlay);
          if (current > 1) refresh(current - 1);
        });
      }
      if (plus) {
        plus.addEventListener('click', () => {
          refresh(this._getQty(overlay) + 1);
        });
      }

      numEl.addEventListener('input', () => {
        const val = parseInt(numEl.value, 10);
        if (!isNaN(val) && val >= 1) refresh(val);
        if (minus) minus.disabled = (parseInt(numEl.value, 10) || 1) <= 1;
      });

      numEl.addEventListener('blur', () => {
        const val = parseInt(numEl.value, 10);
        refresh(isNaN(val) || val < 1 ? 1 : val);
      });

      // Initial state
      if (minus) minus.disabled = true;
    }

    _recalcTotal(overlay, price, compare, qty, moneyFmt) {
      const totalEl = overlay.querySelector('.sa-pgm-total-price');
      if (totalEl) totalEl.textContent = this._fmtMoney(price * qty, moneyFmt);

      const compareEl = overlay.querySelector('.sa-pgm-compare-price-total');
      if (compareEl) {
        if (compare && compare > price) {
          compareEl.textContent = this._fmtMoney(compare * qty, moneyFmt);
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }
    }

    _initATC(overlay) {
      const btn = overlay.querySelector('.sa-pgm-atc');
      if (!btn) return;
      const origText = btn.textContent.trim();

      btn.setAttribute('aria-live', 'polite');

      btn.addEventListener('click', async () => {
        const variantId = btn.dataset.variantId;
        const qty = this._getQty(overlay);
        if (!variantId || btn.disabled) return;

        btn.disabled = true;
        btn.setAttribute('aria-busy', 'true');
        btn.classList.add('loading');

        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ id: parseInt(variantId, 10), quantity: qty }),
          });
          if (!res.ok) throw new Error('');

          btn.classList.remove('loading');
          btn.setAttribute('aria-busy', 'false');
          btn.disabled = false;
          window.location.href = '/cart';
        } catch (_) {
          btn.classList.remove('loading');
          btn.setAttribute('aria-busy', 'false');
          btn.disabled = false;
        }
      });
    }

    _fmtMoney(cents, format) {
      if (window.Shopify?.formatMoney) {
        return Shopify.formatMoney(cents, format);
      }
      return '$' + (cents / 100).toFixed(2);
    }
  }

  customElements.define('sa-product-grid', SaProductGrid);
}
